// client/src/pages/admin/AdminStoreList.js
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Button, IconButton, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Stack, MenuItem, FormControlLabel, Switch, Avatar,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EmptyState from '../../components/EmptyState';
import { API_BASE_URL, getImageUrl } from '../../utils/media';
import { useActingStore } from '../../context/ActingStoreContext';

const emptyCreateForm = {
  slug: '', name: '', domain: '',
  primaryColor: '#3563E9', secondaryColor: '#DC3545',
  successColor: '#22A45D', warningColor: '#ED6C02', errorColor: '#D32F2F',
  logoFile: null, bannerFile: null,
  useBackgroundColor: false, backgroundColor: '#F6F7FB',
  useTextColor: false, textColor: '#1A1A1A',
  address: '', vatNumber: '',
};

// Strips a pasted-in scheme/path/trailing slash down to a bare hostname -
// routing matches the literal Host header a browser sends, which never
// includes "https://" or a path, so anything else here would silently never
// match a real visitor's request.
const normalizeDomain = (value) => value
  .toLowerCase()
  .trim()
  .replace(/^[a-z]+:\/\//, '')
  .replace(/\/.*$/, '');

// Shared by both dialogs: builds the multipart body the backend expects
// (logo/banner as files only when a new one was picked - otherwise the
// existing URL string is round-tripped unchanged; an unchecked color
// override is sent as '' so the backend clears it back to "no override").
const buildStoreFormData = (data) => {
  const body = new FormData();
  body.append('slug', data.slug);
  body.append('name', data.name);
  body.append('domain', data.domain);
  body.append('primaryColor', data.primaryColor);
  body.append('secondaryColor', data.secondaryColor);
  body.append('successColor', data.successColor);
  body.append('warningColor', data.warningColor);
  body.append('errorColor', data.errorColor);
  body.append('backgroundColor', data.useBackgroundColor ? data.backgroundColor : '');
  body.append('textColor', data.useTextColor ? data.textColor : '');
  if (data.logoFile) body.append('logo', data.logoFile);
  else if (data.logoUrl) body.append('logoUrl', data.logoUrl);
  if (data.bannerFile) body.append('banner', data.bannerFile);
  else if (data.bannerUrl) body.append('bannerUrl', data.bannerUrl);
  if (data.supportEmail !== undefined) body.append('supportEmail', data.supportEmail);
  body.append('address', data.address);
  body.append('vatNumber', data.vatNumber);
  if (data.status !== undefined) body.append('status', data.status);
  return body;
};

// A compact "pick an image, see a preview" control used for both logo and banner.
const ImagePickerField = ({ label, helperText, file, existingUrl, onChange, previewHeight = 48 }) => {
  // Only recreated when the picked File actually changes - re-running this
  // on every keystroke elsewhere in the form would leak a fresh blob URL
  // each time instead of reusing the same one.
  const objectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (objectUrl) URL.revokeObjectURL(objectUrl); }, [objectUrl]);
  const previewSrc = objectUrl || (existingUrl ? getImageUrl(existingUrl) : null);
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.5 }}>{label}</Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        {previewSrc && (
          <Avatar src={previewSrc} variant="rounded" sx={{ width: previewHeight, height: previewHeight, bgcolor: 'action.hover' }} />
        )}
        <Button component="label" variant="outlined" size="small" startIcon={<UploadFileIcon />}>
          {previewSrc ? 'Replace' : 'Upload'}
          <input hidden type="file" accept="image/*" onChange={(e) => onChange(e.target.files[0] || null)} />
        </Button>
      </Stack>
      {helperText && <Typography variant="caption" color="text.secondary">{helperText}</Typography>}
    </Box>
  );
};

const ColorOverrideField = ({ label, enabled, color, onToggle, onColorChange }) => (
  <Box>
    <FormControlLabel
      control={<Switch checked={enabled} onChange={(e) => onToggle(e.target.checked)} />}
      label={label}
    />
    {enabled && (
      <TextField
        type="color" fullWidth size="small"
        value={color}
        onChange={(e) => onColorChange(e.target.value)}
      />
    )}
  </Box>
);

const AdminStoreList = () => {
  const { managedStores, refreshManagedStores, setActingStoreId } = useActingStore();
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(emptyCreateForm);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [editingStore, setEditingStore] = useState(null); // the store object being edited, or null
  const [editData, setEditData] = useState(null);
  const [editError, setEditError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [deleteError, setDeleteError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/admin/stores`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: buildStoreFormData(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create store.');
      setFormOpen(false);
      setFormData(emptyCreateForm);
      await refreshManagedStores();
      setActingStoreId(data.id);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (store) => {
    setEditingStore(store);
    setEditData({
      name: store.name,
      slug: store.slug,
      domain: store.domain,
      primaryColor: store.primary_color || '#3563E9',
      secondaryColor: store.secondary_color || '#DC3545',
      successColor: store.success_color || '#22A45D',
      warningColor: store.warning_color || '#ED6C02',
      errorColor: store.error_color || '#D32F2F',
      logoFile: null,
      logoUrl: store.logo_url || '',
      bannerFile: null,
      bannerUrl: store.banner_url || '',
      useBackgroundColor: Boolean(store.background_color),
      backgroundColor: store.background_color || '#F6F7FB',
      useTextColor: Boolean(store.text_color),
      textColor: store.text_color || '#1A1A1A',
      supportEmail: store.support_email || '',
      address: store.address || '',
      vatNumber: store.vat_number || '',
      status: store.status || 'active',
    });
    setEditError('');
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditError('');
    if (editData.domain !== editingStore.domain) {
      const confirmed = window.confirm(
        `Change this store's domain from "${editingStore.domain}" to "${editData.domain}"?\n\n` +
        `Visitors to the OLD domain will stop reaching this store. Make sure DNS for the new domain actually points here.`
      );
      if (!confirmed) return;
    }
    setEditSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/admin/stores/${editingStore.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: buildStoreFormData(editData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update store.');
      setEditingStore(null);
      await refreshManagedStores();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (store) => {
    const confirmed = window.confirm(
      `Delete "${store.name}"? This cannot be undone.\n\n` +
      `This only works if the store has no users, products, orders, or other data attached - otherwise it will be rejected. ` +
      `If it is empty, its database is deleted along with it.`
    );
    if (!confirmed) return;
    setDeleteError('');
    setDeletingId(store.id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/admin/stores/${store.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete store.');
      await refreshManagedStores();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Manage Stores</Typography>
        <Button variant="contained" onClick={() => setFormOpen(true)}>New Store</Button>
      </Box>

      {deleteError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError('')}>{deleteError}</Alert>}

      {managedStores.length === 0 ? (
        <EmptyState
          icon={<StorefrontOutlinedIcon />}
          title="No stores yet"
          description="Create your first store to start managing its products, orders, and branding. Each store gets its own database."
        />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Logo</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>Database</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {managedStores.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Avatar src={s.logo_url ? getImageUrl(s.logo_url) : undefined} variant="rounded" sx={{ width: 32, height: 32 }}>
                      {s.name?.[0]}
                    </Avatar>
                  </TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell>{s.slug}</TableCell>
                  <TableCell>{s.domain}</TableCell>
                  <TableCell>
                    {s.has_own_database
                      ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{s.db_name}</Typography>
                      : <Chip label="Shared" size="small" variant="outlined" title="Still on the shared platform database - move it with migrate-store-to-own-db.js" />}
                  </TableCell>
                  <TableCell><Chip label={s.status} size="small" color={s.status === 'active' ? 'success' : 'default'} /></TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(s)} aria-label={`Edit ${s.name}`}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(s)}
                      disabled={deletingId === s.id}
                      aria-label={`Delete ${s.name}`}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="xs">
        <Box component="form" onSubmit={handleCreate}>
          <DialogTitle>New Store</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {formError && <Alert severity="error">{formError}</Alert>}
              <TextField
                label="Store name" required fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                label="Slug (lowercase, no spaces)" required fullWidth
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              />
              <TextField
                label="Domain (e.g. diveshop.example.com — no https://)" required fullWidth
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: normalizeDomain(e.target.value) })}
              />
              <TextField
                label="Primary color" type="color" fullWidth
                value={formData.primaryColor}
                onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
              />
              <TextField
                label="Secondary color" type="color" fullWidth
                value={formData.secondaryColor}
                onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
              />
              <TextField
                label="Success color" type="color" fullWidth
                helperText="Used for success badges/alerts and in-stock indicators."
                value={formData.successColor}
                onChange={(e) => setFormData({ ...formData, successColor: e.target.value })}
              />
              <TextField
                label="Warning color" type="color" fullWidth
                helperText="Used for low-stock chips and warning alerts."
                value={formData.warningColor}
                onChange={(e) => setFormData({ ...formData, warningColor: e.target.value })}
              />
              <TextField
                label="Error color" type="color" fullWidth
                helperText="Used for delete buttons and error alerts."
                value={formData.errorColor}
                onChange={(e) => setFormData({ ...formData, errorColor: e.target.value })}
              />
              <ImagePickerField
                label="Logo"
                helperText="Shown in the navbar in place of the store name."
                file={formData.logoFile}
                existingUrl={null}
                onChange={(file) => setFormData({ ...formData, logoFile: file })}
              />
              <ImagePickerField
                label="Banner"
                helperText="Shown as the hero image at the top of the homepage."
                file={formData.bannerFile}
                existingUrl={null}
                previewHeight={64}
                onChange={(file) => setFormData({ ...formData, bannerFile: file })}
              />
              <ColorOverrideField
                label="Custom background color"
                enabled={formData.useBackgroundColor}
                color={formData.backgroundColor}
                onToggle={(checked) => setFormData({ ...formData, useBackgroundColor: checked })}
                onColorChange={(color) => setFormData({ ...formData, backgroundColor: color })}
              />
              <ColorOverrideField
                label="Custom text color"
                enabled={formData.useTextColor}
                color={formData.textColor}
                onToggle={(checked) => setFormData({ ...formData, useTextColor: checked })}
                onColorChange={(color) => setFormData({ ...formData, textColor: color })}
              />
              <TextField
                label="Physical address" fullWidth multiline minRows={2}
                helperText="Printed on invoices, quotes, and proforma invoices."
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
              <TextField
                label="VAT number" fullWidth
                helperText="Leave empty if the business isn't VAT-registered."
                value={formData.vatNumber}
                onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>Create Store</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(editingStore)} onClose={() => setEditingStore(null)} fullWidth maxWidth="xs">
        {editData && (
          <Box component="form" onSubmit={handleEdit}>
            <DialogTitle>Edit {editingStore?.name}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {editError && <Alert severity="error">{editError}</Alert>}
                <TextField
                  label="Store name" required fullWidth
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                />
                <TextField
                  label="Slug" required fullWidth
                  helperText="Internal identifier - used as the fallback store if a domain doesn't match anything."
                  value={editData.slug}
                  onChange={(e) => setEditData({ ...editData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                />
                <TextField
                  label="Domain" required fullWidth
                  helperText="Bare hostname only, no https:// or path. Changing it redirects live traffic — make sure DNS is set up first."
                  value={editData.domain}
                  onChange={(e) => setEditData({ ...editData, domain: normalizeDomain(e.target.value) })}
                />
                <TextField
                  label="Primary color" type="color" fullWidth
                  value={editData.primaryColor}
                  onChange={(e) => setEditData({ ...editData, primaryColor: e.target.value })}
                />
                <TextField
                  label="Secondary color" type="color" fullWidth
                  value={editData.secondaryColor}
                  onChange={(e) => setEditData({ ...editData, secondaryColor: e.target.value })}
                />
                <TextField
                  label="Success color" type="color" fullWidth
                  helperText="Used for success badges/alerts and in-stock indicators."
                  value={editData.successColor}
                  onChange={(e) => setEditData({ ...editData, successColor: e.target.value })}
                />
                <TextField
                  label="Warning color" type="color" fullWidth
                  helperText="Used for low-stock chips and warning alerts."
                  value={editData.warningColor}
                  onChange={(e) => setEditData({ ...editData, warningColor: e.target.value })}
                />
                <TextField
                  label="Error color" type="color" fullWidth
                  helperText="Used for delete buttons and error alerts."
                  value={editData.errorColor}
                  onChange={(e) => setEditData({ ...editData, errorColor: e.target.value })}
                />
                <ImagePickerField
                  label="Logo"
                  helperText="Shown in the navbar in place of the store name."
                  file={editData.logoFile}
                  existingUrl={editData.logoUrl}
                  onChange={(file) => setEditData({ ...editData, logoFile: file })}
                />
                <ImagePickerField
                  label="Banner"
                  helperText="Shown as the hero image at the top of the homepage."
                  file={editData.bannerFile}
                  existingUrl={editData.bannerUrl}
                  previewHeight={64}
                  onChange={(file) => setEditData({ ...editData, bannerFile: file })}
                />
                <ColorOverrideField
                  label="Custom background color"
                  enabled={editData.useBackgroundColor}
                  color={editData.backgroundColor}
                  onToggle={(checked) => setEditData({ ...editData, useBackgroundColor: checked })}
                  onColorChange={(color) => setEditData({ ...editData, backgroundColor: color })}
                />
                <ColorOverrideField
                  label="Custom text color"
                  enabled={editData.useTextColor}
                  color={editData.textColor}
                  onToggle={(checked) => setEditData({ ...editData, useTextColor: checked })}
                  onColorChange={(color) => setEditData({ ...editData, textColor: color })}
                />
                <TextField
                  label="Support email" type="email" fullWidth
                  value={editData.supportEmail}
                  onChange={(e) => setEditData({ ...editData, supportEmail: e.target.value })}
                />
                <TextField
                  label="Physical address" fullWidth multiline minRows={2}
                  helperText="Printed on invoices, quotes, and proforma invoices."
                  value={editData.address}
                  onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                />
                <TextField
                  label="VAT number" fullWidth
                  helperText="Leave empty if the business isn't VAT-registered."
                  value={editData.vatNumber}
                  onChange={(e) => setEditData({ ...editData, vatNumber: e.target.value })}
                />
                <TextField
                  select label="Status" fullWidth
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </TextField>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditingStore(null)}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={editSubmitting}>Save Changes</Button>
            </DialogActions>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default AdminStoreList;
